import { TContainerExperience } from "./reducerTypes";
import { useState, useEffect } from "react";
import storage from "local-storage-fallback";

type WindowResize = {
  width: number;
  height: number;
  isPortrait: boolean;
};

export const capitalize = (word) => word.charAt(0).toUpperCase() + word.slice(1);

export const loadBoostlingoScript = (callback) => {
  const script = document.createElement("script");
  script.src = "https://s3.us-east-2.amazonaws.com/cdn.convey911.com/connect/connect.js";
  script.type = "text/javascript";
  script.async = true;
  script.crossOrigin = "anonymous";

  script.onload = () => {
    callback();
  };

  script.onerror = (e) => {
    console.error("Failed to load HumanInterpreter script.", { e });
  };

  document.head.appendChild(script);
};

export const useWindowSize = () => {
  const isPxlDense = window.devicePixelRatio > 1;
  const isTouch = "ontouchstart" in document.documentElement;
  const isPortrait = window.innerHeight > window.innerWidth;
  const [windowSize, setWindowSize] = useState<WindowResize>({
    width: window.innerWidth,
    height: window.innerHeight,
    isPortrait: false
  });

  useEffect(() => {
    function handleResize() {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
        isPortrait: isPortrait && isPxlDense && isTouch
      });
    }
    function handleOrientation(m: MediaQueryListEvent) {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
        isPortrait: m.matches && isPxlDense && isTouch
      });
    }

    window.addEventListener("resize", handleResize);
    window.matchMedia("(orientation: portrait)").addListener(handleOrientation);

    handleResize();

    return () => {
      window.matchMedia("(orientation: portrait)").removeListener(handleOrientation);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return windowSize;
};

export const store = () => {
  return storage;
};

export const getLastLocationTime = (updatedAt) => {
  try {
    const msTimestamp = typeof updatedAt === "string" ? parseInt(updatedAt, 10) : updatedAt;
    const date = new Date(msTimestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffSeconds < 60) {
      return "just now";
    } else if (diffMinutes < 60) {
      return diffMinutes === 1 ? "a minute ago" : `${diffMinutes} minutes ago`;
    } else if (diffHours < 24) {
      return diffHours === 1 ? "an hour ago" : `${diffHours} hours ago`;
    } else if (diffDays < 30) {
      return diffDays === 1 ? "a day ago" : `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString();
    }
  } catch (e) {
    return updatedAt;
  }
};

export const formatPhoneNumber = (str) => {
  let phoneNumber = str;
  if (str.startsWith("%2B")) {
    phoneNumber = str.slice(3);
  }
  const cleaned = ("" + phoneNumber).replace(/\D/g, "");

  const match = cleaned.match(/^(?:1)?(\d{3})(\d{3})(\d{4})$/);

  if (match) {
    return `(${match[1]}) ${match[2]}-${match[3]}`;
  }

  return str;
};

export const writeExpire = (key, value, expiry) => {
  const item = {
    value: JSON.stringify(value),
    expiry
  };
  store().setItem(key, JSON.stringify(item));
};

export const readExpire = (key: string) => {
  const itemStr = store().getItem(key);
  if (!itemStr) {
    return null;
  }
  const item = JSON.parse(itemStr);
  const now = new Date();
  if (now.getTime() > item.expiry) {
    store().removeItem(key);
    return null;
  }
  return JSON.parse(item.value);
};

export const formatSentence = (text: string): string => {
  const re = /([!?.]\s+)([a-z])/g;
  return text.replace(re, (m, $1, $2) => $1 + $2.toUpperCase());
};

export const extractStyles = ($element) => {
  // @ts-ignore
  if (!window.ConveyDefaultStyles) {
    const dummy = document.createElement($element.nodeName);
    document.body.appendChild(dummy);
    // @ts-ignore
    window.ConveyDefaultStyles = getComputedStyle(dummy);
    // dummy is kept in DOM because getComputedStyle results are live — detaching would invalidate the cache.
  }
  // @ts-ignore
  const defaultStyles = window.ConveyDefaultStyles;
  const elementStyles = getComputedStyle($element);

  let s = "";
  for (const key in elementStyles) {
    // @ts-ignore
    const defaultKey = key.replace(/-([a-z])/g, function (g) {
      return g[1].toUpperCase();
    });
    if (elementStyles[key] && defaultStyles[defaultKey] !== elementStyles[key]) {
      const styleKey = key.replace(/[A-Z]/g, (match, offset) => (offset > 0 ? "-" : "") + match.toLowerCase());
      if (elementStyles.getPropertyValue(styleKey)) {
        s += styleKey + ":" + elementStyles.getPropertyValue(styleKey) + ";";
      }
    }
  }

  return s;
};

export const useVisualViewportResize = () => {
  const [viewport, setViewport] = useState<{ height: number; offsetTop: number } | null>(null);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;

    const onResize = () => {
      const viewportHeight = vv.height;
      const windowHeight = window.innerHeight;
      // Only apply when keyboard is likely open (viewport significantly smaller than window)
      if (windowHeight - viewportHeight > 100) {
        setViewport({ height: viewportHeight, offsetTop: vv.offsetTop });
      } else {
        setViewport(null);
      }
    };

    vv.addEventListener("resize", onResize);
    vv.addEventListener("scroll", onResize);
    return () => {
      vv.removeEventListener("resize", onResize);
      vv.removeEventListener("scroll", onResize);
    };
  }, []);

  return viewport;
};

/**
 * Resizes the container element and its parent to match the visual viewport
 * when the iOS virtual keyboard is open. This ensures the entire container
 * chain fits within the visible area, keeping fixed headers/inputs in view.
 */
export const useVisualViewportContainerResize = (
  $element: HTMLElement | null,
  viewport: { height: number; offsetTop: number } | null
) => {
  useEffect(() => {
    if (!$element) return;

    const parent = $element.parentElement;

    if (viewport) {
      const heightPx = `${viewport.height}px`;
      // Resize the inner wrapper element (e.g. #smsContainer-XXX-sms)
      $element.style.height = heightPx;
      $element.style.maxHeight = heightPx;
      // Resize the outer container (e.g. #smsContainer) which has 100dvh
      if (parent) {
        parent.style.height = heightPx;
        parent.style.maxHeight = heightPx;
      }
    } else {
      // Restore original values when keyboard is closed
      $element.style.height = "100%";
      $element.style.maxHeight = "";
      if (parent) {
        parent.style.height = "100dvh";
        parent.style.maxHeight = "";
      }
    }
  }, [viewport, $element]);
};

export const delay = (ms) => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

export const appendAsChild = (id, $element, container, experience: TContainerExperience) => {
  const fullId = `${container}-${(id || "").replace(/\D/g, "")}-${experience}`;
  const $existingElement = document.getElementById(fullId);
  const $containerElement = document.getElementById(container);
  const styles = extractStyles($containerElement);

  if ($existingElement) {
    $existingElement.style.cssText = styles;

    return {
      fullId,
      $newElement: $existingElement
    };
  } else {
    const $newElement = document.createElement("div");
    $newElement.style.cssText = styles;
    $newElement.setAttribute("id", fullId);
    $containerElement.appendChild($newElement);

    return {
      fullId,
      $newElement
    };
  }
};
